import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Star, MapPin, Users, Clock, ChefHat, Award, Heart, Search, Loader2, LogOut } from "lucide-react";
import AuthModal from "./components/Auth";
import AdminDashboard from "./components/AdminDashboard";
import ChefDashboard from "./components/ChefDashboard";
import { ClientBookings, ReviewsList } from "./components/ReviewSystem";
import { initializeFirebaseData, checkFirebaseData } from './firebase/initData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [searchCity, setSearchCity] = useState("");
  const [searchRadius, setSearchRadius] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [workshops, setWorkshops] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [user, setUser] = useState(null);
  const [currentView, setCurrentView] = useState('home'); // 'home', 'admin', 'chef'
  const [selectedChef, setSelectedChef] = useState(null);
  const [showChefModal, setShowChefModal] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Check for existing login on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Fetch workshops from backend API
  const fetchWorkshops = async () => {
    try {
      const response = await axios.get(`${API}/workshops`);
      setWorkshops(response.data);
    } catch (error) {
      console.error("Error fetching workshops:", error);
      setError("Erreur lors du chargement des ateliers");
    }
  };

  // Fetch chefs from backend API
  const fetchChefs = async () => {
    try {
      const response = await axios.get(`${API}/chefs`);
      setChefs(response.data);
    } catch (error) {
      console.error("Error fetching chefs:", error);
      setError("Erreur lors du chargement des formateurs");
    }
  };

  // Initialize data on component mount
  const initializeData = async () => {
    try {
      setLoading(true);
      
      // Initialiser Firebase avec les données CETROBON
      console.log('🔥 Initialisation des données Firebase...');
      await initializeFirebaseData();
      console.log('✅ Firebase initialisé avec succès');
      
      await Promise.all([fetchWorkshops(), fetchChefs()]);
    } catch (error) {
      console.error("Error initializing data:", error);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log("Backend API:", response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
    initializeData();
  }, []);

  // Handle search with backend integration
  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchLoading(true);
    
    try {
      const searchData = {
        city: searchCity || undefined,
        radius: searchRadius ? parseInt(searchRadius) : undefined,
        date: searchDate || undefined
      };

      // Remove undefined values
      Object.keys(searchData).forEach(key => 
        searchData[key] === undefined && delete searchData[key]
      );

      const response = await axios.post(`${API}/workshops/search`, searchData);
      setWorkshops(response.data);
      console.log("Recherche effectuée:", searchData, "Résultats:", response.data.length);
    } catch (error) {
      console.error("Error searching workshops:", error);
      setError("Erreur lors de la recherche");
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle login
  const handleLogin = (userData) => {
    setUser(userData);
    setShowAuthModal(false);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setCurrentView('home');
  };

  // Navigate to dashboard based on user role
  const navigateToDashboard = () => {
    if (user?.role === 'admin') {
      setCurrentView('admin');
    } else if (user?.role === 'chef') {
      setCurrentView('chef');
    }
  };

  // Open chef profile modal
  const openChefProfile = (chef) => {
    setSelectedChef(chef);
    setShowChefModal(true);
  };

  // Open messaging with chef
  const openMessaging = async (chef) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    setSelectedChef(chef);
    setShowChefModal(false);
    setShowMessaging(true);
    
    // Load or create conversation with this chef
    await loadConversationWithChef(chef.id);
  };

  // Load conversation with specific chef
  const loadConversationWithChef = async (chefId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Try to find existing conversation
      const conversationsRes = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const existingConv = conversationsRes.data.find(conv => 
        conv.participant_ids.includes(chefId)
      );
      
      if (existingConv) {
        setCurrentConversation(existingConv);
        loadMessages(existingConv.id);
      } else {
        // Will create conversation when first message is sent
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error loading conversation:", error);
    }
  };

  // Load messages for a conversation
  const loadMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  // Send a message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChef) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/messages`, {
        recipient_id: selectedChef.id,
        content: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add message to local state
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      
      // Update conversation if it was created
      if (!currentConversation) {
        await loadConversationWithChef(selectedChef.id);
      }
      
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Erreur lors de l'envoi du message");
    }
  };

  // Render admin dashboard
  if (currentView === 'admin' && user?.role === 'admin') {
    return <AdminDashboard user={user} onClose={() => setCurrentView('home')} />;
  }

  // Render chef dashboard
  if (currentView === 'chef' && user?.role === 'chef') {
    return <ChefDashboard user={user} onClose={() => setCurrentView('home')} />;
  }

  // Render client bookings/reviews page
  if (currentView === 'bookings' && user?.role === 'client') {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <h1 className="text-2xl font-bold text-orange-600">Mes Réservations</h1>
              <Button variant="outline" onClick={() => setCurrentView('home')}>
                Retour au site
              </Button>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ClientBookings user={user} />
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-orange-600" />
          <p className="text-gray-600">Chargement des ateliers culinaires...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl sm:text-2xl font-bold text-orange-600">CETROBON</h1>
              <span className="ml-2 text-xs sm:text-sm text-gray-500">.fr</span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-6 items-center">
              <a href="#" className="text-gray-700 hover:text-orange-600 transition-colors text-sm">Accueil</a>
              <a href="#" className="text-gray-700 hover:text-orange-600 transition-colors text-sm">Offrir un cours</a>
              <a href="#" className="text-gray-700 hover:text-orange-600 transition-colors text-sm">Devenir formateur</a>
              <a 
                href="#"
                className="text-orange-600 hover:text-orange-700 font-medium transition-colors text-sm"
                onClick={(e) => {
                  e.preventDefault();
                  const workshopsSection = document.querySelector('[data-section="workshops"]');
                  if (workshopsSection) {
                    workshopsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Découvrir les ateliers
              </a>
            </nav>
            
            {/* User Actions */}
            <div className="flex space-x-2 items-center">
              {user ? (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 hidden xl:block">
                    <strong>{user.name}</strong>
                  </span>
                  {(user.role === 'admin' || user.role === 'chef') && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={navigateToDashboard}
                      className="text-xs px-2 py-1 border-orange-600 text-orange-600 hover:bg-orange-50"
                    >
                      {user.role === 'admin' ? 'Admin' : 'Chef'}
                    </Button>
                  )}
                  {user.role === 'client' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setCurrentView('bookings')}
                      className="text-xs px-2 py-1 border-orange-600 text-orange-600 hover:bg-orange-50 hidden sm:block"
                    >
                      Réservations
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={handleLogout}
                    className="text-xs px-2 py-1"
                  >
                    <LogOut className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAuthModal(true)}
                    className="text-xs px-3 py-1"
                  >
                    Connexion
                  </Button>
                  <Button 
                    size="sm" 
                    className="bg-orange-600 hover:bg-orange-700 text-xs px-3 py-1"
                    onClick={() => setShowAuthModal(true)}
                  >
                    Inscription
                  </Button>
                </div>
              )}
              
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </Button>
            </div>
          </div>
          
          {/* Mobile Navigation */}
          {showMobileMenu && (
            <div className="lg:hidden border-t border-gray-200 py-3">
              <nav className="flex flex-col space-y-3">
                <a href="#" className="text-gray-700 hover:text-orange-600 transition-colors text-sm">Accueil</a>
                <a href="#" className="text-gray-700 hover:text-orange-600 transition-colors text-sm">Offrir un cours</a>
                <a href="#" className="text-gray-700 hover:text-orange-600 transition-colors text-sm">Devenir formateur</a>
                <a 
                  href="#"
                  className="text-orange-600 hover:text-orange-700 font-medium transition-colors text-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    const workshopsSection = document.querySelector('[data-section="workshops"]');
                    if (workshopsSection) {
                      workshopsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                    setShowMobileMenu(false);
                  }}
                >
                  Découvrir les ateliers
                </a>
                {user?.role === 'client' && (
                  <a 
                    href="#"
                    className="text-gray-700 hover:text-orange-600 transition-colors text-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentView('bookings');
                      setShowMobileMenu(false);
                    }}
                  >
                    Mes Réservations
                  </a>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white shadow-md py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 items-center">
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Input
                placeholder="Ville (ex: Paris, Lyon...)"
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                className="flex-1 text-sm"
              />
              <Input
                placeholder="Distance (ex: 10km, 25km)"
                value={searchRadius}
                onChange={(e) => setSearchRadius(e.target.value)}
                className="w-full sm:w-48 text-sm"
                title="Distance en kilomètres autour de la ville recherchée"
              />
              <Input
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                className="w-full sm:w-40 text-sm"
                title="Date souhaitée pour l'atelier"
              />
            </div>
            <Button 
              type="submit" 
              className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto px-4 sm:px-8 text-sm"
              disabled={searchLoading}
            >
              <Search className="w-4 h-4 mr-2" />
              {searchLoading ? "Recherche..." : "Rechercher"}
            </Button>
          </form>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url('https://images.unsplash.com/photo-1556911220-bff31c812dba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxraXRjaGVufGVufDB8fHx8MTc1NzM2MDQzNXww&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Apprenez à cuisiner avec des <span className="text-orange-400">passionnés</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200">
            Découvrez des ateliers culinaires près de chez vous, animés par des chefs locaux
          </p>
          <Button 
            size="lg" 
            className="bg-orange-600 hover:bg-orange-700 text-lg px-8 py-3"
            onClick={() => {
              const workshopsSection = document.querySelector('[data-section="workshops"]');
              if (workshopsSection) {
                workshopsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            Découvrir les ateliers
          </Button>
        </div>
      </section>

      {/* Why CETROBON */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Pourquoi CETROBON ?</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ChefHat className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Cours animés par des passionnés</h3>
              <p className="text-gray-600">Des chefs locaux partagent leur savoir-faire avec enthousiasme</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Matériel fourni</h3>
              <p className="text-gray-600">Tous les ustensiles et ingrédients sont inclus dans le prix</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Chez vous ou chez eux</h3>
              <p className="text-gray-600">Cours à domicile ou dans l'atelier du chef selon vos préférences</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Idéal en cadeau</h3>
              <p className="text-gray-600">Offrez une expérience unique et inoubliable à vos proches</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ateliers proches */}
      <section className="py-16 bg-gray-50" data-section="workshops">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Ateliers proches de chez vous</h2>
          {error && (
            <div className="text-center text-red-600 mb-8 p-4 bg-red-50 rounded-lg">
              {error}
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-8">
            {workshops.length > 0 ? workshops.map((workshop) => (
              <Card key={workshop.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                <div className="relative">
                  <img 
                    src={workshop.image_url || "https://images.unsplash.com/photo-1507048331197-7d4ac70811cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjb29raW5nJTIwY2xhc3N8ZW58MHx8fHwxNzU3MzYwNDIzfDA&ixlib=rb-4.1.0&q=85"} 
                    alt={workshop.title}
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                  <Badge className="absolute top-3 right-3 bg-orange-600">{workshop.category}</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{workshop.title}</CardTitle>
                  <CardDescription className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {workshop.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Chef {workshop.chef_name}</span>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm ml-1">{workshop.rating} ({workshop.reviews_count})</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {workshop.duration}
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      Max {workshop.max_participants}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-3">
                  <div className="flex justify-between items-center w-full">
                    <span className="text-2xl font-bold text-orange-600">{workshop.price}€</span>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                      <span className="text-sm text-gray-600">{workshop.reviews_count} avis</span>
                    </div>
                  </div>
                  <div className="flex space-x-2 w-full">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        // TODO: Show workshop details with reviews
                        console.log("Show reviews for workshop:", workshop.id);
                      }}
                    >
                      Voir avis
                    </Button>
                    <Button className="bg-orange-600 hover:bg-orange-700 flex-1">
                      Réserver
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            )) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-500">Aucun atelier trouvé. Essayez une autre recherche.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Nos formateurs */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Nos formateurs passionnés</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {chefs.length > 0 ? chefs.map((chef) => (
              <Card key={chef.id} className="text-center hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden">
                    <img 
                      src={chef.image_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjaGVmfGVufDB8fHx8MTc1NzM2MDQzNXww&ixlib=rb-4.1.0&q=85"} 
                      alt={chef.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardTitle>{chef.name}</CardTitle>
                  <CardDescription>{chef.specialty}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-center items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                      {chef.rating}
                    </div>
                    <div>
                      {chef.workshops_count} ateliers
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => openChefProfile(chef)}
                  >
                    Voir le profil
                  </Button>
                </CardFooter>
              </Card>
            )) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-500">Aucun formateur trouvé.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cadeaux & coffrets */}
      <section className="py-16 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Offrez une expérience culinaire unique</h2>
          <p className="text-lg text-gray-600 mb-8">
            Cartes cadeaux et coffrets d'expérience pour faire plaisir à coup sûr
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
              Offrir un atelier
            </Button>
            <Button size="lg" variant="outline">
              Voir les coffrets
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Questions fréquentes</h2>
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold mb-2">Les ateliers sont-ils remboursables ?</h3>
              <p className="text-gray-600">Oui, vous pouvez annuler gratuitement jusqu'à 48h avant le début de l'atelier.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold mb-2">Le matériel de cuisine est-il fourni ?</h3>
              <p className="text-gray-600">Absolument ! Tous les ustensiles, équipements et ingrédients sont inclus dans le prix.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold mb-2">Puis-je offrir un atelier en cadeau ?</h3>
              <p className="text-gray-600">Bien sûr ! Nos cartes cadeaux sont parfaites pour offrir une expérience culinaire unique.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold mb-2">Y a-t-il une assurance en cas d'accident ?</h3>
              <p className="text-gray-600">Tous nos ateliers sont couverts par une assurance responsabilité civile professionnelle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">CETROBON</h3>
              <p className="text-gray-400">La plateforme de référence pour apprendre la cuisine avec des passionnés.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Découvrir</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Ateliers près de moi</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Tous les formateurs</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cartes cadeaux</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Formateurs</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Devenir formateur</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Espace formateur</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Support</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Aide</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">CGV</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 CETROBON. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={handleLogin}
      />

      {/* Chef Profile Modal */}
      {selectedChef && (
        <Dialog open={showChefModal} onOpenChange={setShowChefModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-orange-600">
                {selectedChef.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Chef Header */}
              <div className="flex items-start space-x-6">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                  <img 
                    src={selectedChef.image_url && selectedChef.image_url.startsWith('http') 
                      ? selectedChef.image_url 
                      : selectedChef.image_url 
                        ? `${BACKEND_URL}${selectedChef.image_url}`
                        : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjaGVmfGVufDB8fHx8MTc1NzM2MDQzNXww&ixlib=rb-4.1.0&q=85"
                    } 
                    alt={selectedChef.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">{selectedChef.specialty}</h3>
                  <div className="flex items-center mt-2 space-x-4">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-500 mr-1" />
                      <span className="text-gray-600">{selectedChef.location}</span>
                    </div>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                      <span className="text-gray-600">{selectedChef.rating}/5</span>
                    </div>
                    <div className="flex items-center">
                      <ChefHat className="w-4 h-4 text-gray-500 mr-1" />
                      <span className="text-gray-600">{selectedChef.workshops_count} ateliers</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedChef.bio && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">À propos</h4>
                  <p className="text-gray-700 leading-relaxed">{selectedChef.bio}</p>
                </div>
              )}

              {/* Statistics */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{selectedChef.rating}</div>
                  <div className="text-sm text-gray-600">Note moyenne</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{selectedChef.workshops_count}</div>
                  <div className="text-sm text-gray-600">Ateliers créés</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {new Date(selectedChef.created_at).getFullYear()}
                  </div>
                  <div className="text-sm text-gray-600">Depuis</div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex space-x-3 pt-4">
                <Button className="flex-1 bg-orange-600 hover:bg-orange-700">
                  Voir les ateliers
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => openMessaging(selectedChef)}
                >
                  Envoyer un message
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Messaging Modal */}
      {showMessaging && selectedChef && (
        <Dialog open={showMessaging} onOpenChange={setShowMessaging}>
          <DialogContent className="max-w-2xl h-[600px] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-orange-600 flex items-center">
                <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                  <img 
                    src={selectedChef.image_url && selectedChef.image_url.startsWith('http') 
                      ? selectedChef.image_url 
                      : selectedChef.image_url 
                        ? `${BACKEND_URL}${selectedChef.image_url}`
                        : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjaGVmfGVufDB8fHx8MTc1NzM2MDQzNXww&ixlib=rb-4.1.0&q=85"
                    } 
                    alt={selectedChef.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div>{selectedChef.name}</div>
                  <div className="text-sm text-gray-600 font-normal">{selectedChef.specialty}</div>
                </div>
              </DialogTitle>
            </DialogHeader>
            
            {/* Messages area */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50 rounded-lg mb-4 space-y-3">
                {messages.length > 0 ? messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.sender_id === user?.id 
                          ? 'bg-orange-600 text-white' 
                          : 'bg-white text-gray-900 border'
                      }`}
                    >
                      <p className="text-sm">{message.filtered_content}</p>
                      {message.is_filtered && (
                        <p className="text-xs mt-1 opacity-75">
                          ⚠️ Message modéré automatiquement
                        </p>
                      )}
                      <p className={`text-xs mt-1 ${
                        message.sender_id === user?.id ? 'text-orange-200' : 'text-gray-500'
                      }`}>
                        {new Date(message.created_at).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )) : (
                  <div className="text-center text-gray-500 py-8">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-orange-600" />
                    </div>
                    <p className="text-lg font-semibold mb-2">Commencez la conversation !</p>
                    <p className="text-sm">Envoyez votre premier message à {selectedChef.name}</p>
                    <div className="mt-4 p-3 bg-orange-50 rounded-lg text-left">
                      <p className="text-xs text-orange-800 font-semibold mb-1">💡 Conseils pour votre message :</p>
                      <ul className="text-xs text-orange-700 space-y-1">
                        <li>• Présentez-vous brièvement</li>
                        <li>• Mentionnez quel type d'atelier vous intéresse</li>
                        <li>• Posez des questions sur les disponibilités</li>
                        <li>• Restez poli et enthousiaste !</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Message input */}
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={`Écrivez votre message à ${selectedChef.name}...`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                />
                <Button 
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="bg-orange-600 hover:bg-orange-700 px-6"
                >
                  Envoyer
                </Button>
              </div>
              
              {/* Security notice */}
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-800">
                  🛡️ <strong>Messagerie sécurisée CETROBON</strong> : 
                  Vos conversations sont protégées. Les tentatives de partage d'informations de contact 
                  sont automatiquement modérées pour votre sécurité.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />}>
            <Route index element={<Home />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;